import {
  Alert,
  Box,
  Button,
  FormHelperText,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import {
  FieldArray,
  Form,
  Formik,
  FormikErrors,
  useFormikContext,
} from 'formik';
import AdornedCurrencyInput from '@/components/common/AdornedCurrencyInput';
import { useEffect } from 'react';
import { HistoricalValuesFormState } from './useHistoricalValuesForm';
import Delete from '@mui/icons-material/Delete';
import Add from '@mui/icons-material/Add';
import dayjs from '@/config/dayjs';
import { useLocalization } from '@/hooks/useLocalization';
import SingleDate from '@/components/date/SingleDate';
import {
  getHistoricalValuesFormErrors,
  HistoricalValueRowFormikErrors,
} from './historicalValuesRowValidation';

interface HistoricalValueFormObserver {
  onFormChange: (state: HistoricalValuesFormState) => void;
}

/**
 * To pull the values out of the form, we're using `useFormikContext`.
 * This pattern is different than previous flows since we're using
 * the `<Formik>` component in order to use `<FieldArray>`.
 */
const HistoricalValueFormObserver = ({
  onFormChange,
}: HistoricalValueFormObserver) => {
  const { values, isValid } = useFormikContext<HistoricalValuesFormValues>();

  useEffect(() => {
    onFormChange({
      values: {
        DidHistoricalChange: values.DidHistoricalChange,
        HistoricalValues: values.HistoricalValues,
      },
      isValid,
    });
    // Passing in onFormChange causes infinite rendering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid, values]);

  return null;
};

interface HistoricalValue {
  EffectiveDate: string;
  Balance: string;
  virtualId: string;
}

export interface HistoricalValuesFormValues {
  DidHistoricalChange: boolean;
  HistoricalValues: HistoricalValue[];
}

interface HistoricValuesFormProps {
  currentValue: string;
  initialValues: HistoricalValuesFormValues;
  onFormChange: (state: HistoricalValuesFormState) => void;
}

const emptyRow = (): HistoricalValue => ({
  EffectiveDate: '',
  Balance: '',
  virtualId: crypto.randomUUID(),
});

const HistoricalValuesForm = ({
  initialValues,
  onFormChange,
  currentValue,
}: HistoricValuesFormProps) => {
  const { t } = useLocalization();

  const today = dayjs();
  const defaultValues: HistoricalValuesFormValues = {
    HistoricalValues: [
      {
        EffectiveDate: today.format('MM/DD/YYYY'),
        Balance: currentValue || '0.00',
        virtualId: crypto.randomUUID(),
      },
      emptyRow(),
      emptyRow(),
    ],
    DidHistoricalChange: true,
  };

  const dataSet =
    initialValues.HistoricalValues.length === 0 ? defaultValues : initialValues;

  const validateHistoricalValues = (values: HistoricalValuesFormValues) => {
    const rowErrors = getHistoricalValuesFormErrors(values.HistoricalValues);
    if (!rowErrors) {
      return {};
    }
    return {
      HistoricalValues: rowErrors,
    } satisfies FormikErrors<HistoricalValuesFormValues>;
  };

  const getRowErrors = (
    errors: FormikErrors<HistoricalValuesFormValues>,
    index: number,
  ): HistoricalValueRowFormikErrors | undefined => {
    const historicalErrors = errors.HistoricalValues;
    if (!Array.isArray(historicalErrors)) {
      return undefined;
    }
    const rowErrors = historicalErrors[index];
    if (!rowErrors || typeof rowErrors !== 'object') {
      return undefined;
    }
    return rowErrors as HistoricalValueRowFormikErrors;
  };

  return (
    <Formik
      initialValues={dataSet}
      validate={validateHistoricalValues}
      validateOnChange
      validateOnBlur
      onSubmit={() => {}}
    >
      {({ values, errors, setFieldValue }) => (
        <>
          <HistoricalValueFormObserver onFormChange={onFormChange} />
          <Form>
            <FieldArray
              name='HistoricalValues'
              render={(arrayHelpers) => (
                <Box mb={4}>
                  {values.HistoricalValues.length > 0 &&
                    values.HistoricalValues.map((item, index) => {
                      const rowErrors = getRowErrors(errors, index);

                      return (
                        <Box
                          key={`item-${item.virtualId}`}
                          sx={{ marginBottom: 2 }}
                        >
                          <Grid
                            container
                            spacing={2}
                            sx={{ alignItems: 'center' }}
                          >
                            <Grid size={{ sm: 4, xs: 12 }}>
                              {/* small & up */}
                              <Box
                                sx={{ display: { xs: 'none', sm: 'block' } }}
                              >
                                <Typography
                                  variant='body2'
                                  sx={{
                                    display:
                                      index === 0 ? 'inline-block' : 'none',
                                  }}
                                >
                                  {t(
                                    'addAccountModal.historicalValues.dateRange',
                                  )}
                                </Typography>
                              </Box>
                              {/* xs */}
                              <Typography
                                variant='body2'
                                sx={{ display: { xs: 'block', sm: 'none' } }}
                              >
                                {t(
                                  'addAccountModal.historicalValues.dateRange',
                                )}
                              </Typography>

                              <SingleDate
                                date={item.EffectiveDate}
                                onChange={(value) => {
                                  void setFieldValue(
                                    `HistoricalValues.${String(index)}.EffectiveDate`,
                                    value,
                                  );
                                }}
                                label=''
                              />
                            </Grid>
                            <Grid size={{ sm: 4, xs: 12 }}>
                              {/* small & up */}
                              <Box
                                sx={{ display: { xs: 'none', sm: 'block' } }}
                              >
                                <Typography
                                  variant='body2'
                                  sx={{
                                    display:
                                      index === 0 ? 'inline-block' : 'none',
                                  }}
                                >
                                  {t(
                                    'addAccountModal.historicalValues.balance',
                                  )}
                                </Typography>
                              </Box>
                              {/* xs */}
                              <Typography
                                variant='body2'
                                sx={{ display: { xs: 'block', sm: 'none' } }}
                              >
                                {t('addAccountModal.historicalValues.balance')}
                              </Typography>
                              <AdornedCurrencyInput
                                name={`HistoricalValues.${String(index)}.Balance`}
                                allowDecimals
                                decimalsLimit={2}
                                fullWidth
                                aria-label={t(
                                  'addAccountModal.historicalValues.balance',
                                )}
                                value={item.Balance}
                                onValueChange={(value) => {
                                  void setFieldValue(
                                    `HistoricalValues.${String(index)}.Balance`,
                                    value,
                                  );
                                }}
                                disabled={!item.EffectiveDate}
                              />
                            </Grid>
                            <Grid size='grow'>
                              {/* small & up */}
                              <Box
                                sx={{ display: { xs: 'none', sm: 'block' } }}
                              >
                                <Typography
                                  variant='body2'
                                  sx={{
                                    display:
                                      index === 0 ? 'inline-block' : 'none',
                                  }}
                                >
                                  &nbsp;
                                </Typography>
                              </Box>
                              <IconButton
                                sx={{
                                  display: { xs: 'none', sm: 'inline-flex' },
                                }}
                                aria-label={t('delete')}
                                onClick={() => {
                                  arrayHelpers.remove(index);
                                }}
                              >
                                <Delete />
                              </IconButton>

                              {/* xs */}
                              <Button
                                sx={{
                                  display: { xs: 'block', sm: 'none' },
                                  marginBottom: 4,
                                }}
                                variant='outlined'
                                color='error'
                                fullWidth
                                onClick={() => {
                                  arrayHelpers.remove(index);
                                }}
                              >
                                {t('delete')}
                              </Button>
                            </Grid>
                          </Grid>

                          <Grid
                            container
                            spacing={2}
                            sx={{ alignItems: 'center' }}
                          >
                            <Grid size={{ sm: 8, xs: 12 }}>
                              {rowErrors?.Balance === 'missingValue' && (
                                <FormHelperText error>
                                  {t(
                                    'addAccountModal.historicalValues.validation.valueRequired',
                                  )}
                                </FormHelperText>
                              )}

                              {rowErrors?.EffectiveDate === 'missingDate' && (
                                <FormHelperText error>
                                  {t(
                                    'addAccountModal.historicalValues.validation.dateRequired',
                                  )}
                                </FormHelperText>
                              )}

                              {rowErrors?.rowError === 'duplicateDate' && (
                                <Alert
                                  severity='warning'
                                  icon={false}
                                  sx={{ marginY: 2 }}
                                >
                                  {t(
                                    'addAccountModal.historicalValues.validation.duplicateDate',
                                  )}
                                </Alert>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      );
                    })}

                  <Button
                    variant='text'
                    onClick={() => {
                      arrayHelpers.push(emptyRow());
                    }}
                  >
                    <Add />
                    {t('addAccountModal.historicalValues.addAnotherRange')}
                  </Button>
                </Box>
              )}
            />
          </Form>
        </>
      )}
    </Formik>
  );
};

export default HistoricalValuesForm;
